<script>
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import ClientEmptyState from '@modules/consolidator/components/client-empty-state/client-empty-state.vue'

export default {
  name: 'WalletEmptyState',
  components: { FeedbackWrapper, ClientEmptyState },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    hasSelectedClient: {
      type: Boolean,
      default: false,
    },
    hasAccess: {
      type: Boolean,
      default: false,
    },
    hasClients: {
      type: Boolean,
      default: false,
    },
    hasAssets: {
      type: Boolean,
      default: false,
    },
    hasClient: {
      type: Boolean,
      default: false,
    },
    isClientUser: {
      type: Boolean,
      default: false,
    },
    movements: {
      type: Object,
      default: () => ({}),
    },
    investmentType: {
      type: String,
      default: '',
    },
    clientNotSelected: {
      type: Boolean,
      default: false,
    },
    clientsLoading: {
      type: Boolean,
      default: false,
    },
    viewTipo: {
      type: String,
      default: '',
    },
    grupo: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ehVisaoDeGrupo() {
      return this.viewTipo === 'grupo'
    },
    tituloSemSelecao() {
      return this.ehVisaoDeGrupo
        ? 'Você não possui nenhum grupo familiar selecionado'
        : 'Você não possui nenhum cliente selecionado'
    },
    descricaoSemSelecao() {
      return this.ehVisaoDeGrupo
        ? 'Escolha um grupo no seletor acima para ver a carteira consolidada dos membros. Você também pode criar um novo grupo.'
        : 'Selecione um cliente para acompanhar 100% do share da carteira dele, independente da instituição. Você também pode cadastrar um novo cliente na base.'
    },
    rotuloAcaoSemSelecao() {
      return this.ehVisaoDeGrupo ? 'Adicionar grupo' : 'Selecionar cliente'
    },
    rescueMessage() {
      if (this.hasInvestmentTypeContext && !this.hasAssets) {
        return 'Você não possui ativos no tipo de investimento selecionado.'
      }
      if (this.hasAnyMovement) {
        return this.grupo
          ? 'Todos os ativos dos membros deste grupo foram resgatados'
          : 'Todos os ativos do cliente foram resgatados'
      }
      return this.grupo
        ? 'Nenhum membro deste grupo possui ativo cadastrado'
        : 'Cliente selecionado não possui nenhum ativo cadastrado'
    },
    hasInvestmentTypeContext() {
      const rawValue = String(this.investmentType || '')
      return rawValue.trim().length > 0
    },
    isOffshoreView() {
      const rawValue = String(this.investmentType || '')
      const normalizedValue = rawValue.trim().toLowerCase()
      return normalizedValue.indexOf('offshore') !== -1
    },
    hasAnyMovement() {
      if (!this.movements || typeof this.movements !== 'object') {
        return false
      }
      return Object.keys(this.movements).length > 0
    },
    shouldShowNoClientSelectedCard() {
      if (this.isLoading) return false
      if (this.hasSelectedClient) return false
      return this.hasClients || this.clientsLoading
    },
    shouldShowNoAccessCard() {
      if (this.isLoading) return false
      if (!this.hasClients) return false
      if (!this.hasSelectedClient) return false
      return this.hasAccess
    },
    shouldShowNoClientsRegisteredCard() {
      if (this.isLoading) return false
      if (this.isClientUser) return false
      if (this.clientsLoading) return false
      return !this.hasClients
    },
    shouldShowNoAssetsCard() {
      if (this.isLoading) return false
      if (this.isClientUser) return false
      if (!this.hasClient) return false
      if (!this.hasClients) return false
      if (!this.hasSelectedClient) return false
      if (this.hasAssets) return false
      return true
    },
    shouldShowClientNoAssetsCard() {
      if (this.isLoading) return false
      if (!this.isClientUser) return false
      if (this.hasAssets) return false
      return true
    },
    isLoading() {
      return this.loading && !this.clientNotSelected
    },
  },
  methods: {
    handleSendEmail() {
      this.$emit('send-email')
    },
    handleCollectClientDefault() {
      this.$emit('collect-client-default')
    },
    handleAcaoPrimariaSemSelecao() {
      this.$emit(this.ehVisaoDeGrupo ? 'new-group' : 'select-client')
    },
    handleOpenNewClient() {
      this.$emit('open-new-client')
    },
    handleNewAsset() {
      this.$emit('new-asset')
    },
    handleImportAssets() {
      this.$emit('import-assets')
    },
    handleNewMovement() {
      this.$emit('new-movement')
    },
  },
}
</script>

<template>
  <div class="wallet-empty-state">
    <v-row v-if="shouldShowNoClientSelectedCard">
      <v-col cols="12">
        <FeedbackWrapper
          :noData="true"
          icon-name="users"
          icon-color="primary"
          class="az-card-consolidator"
        >
          <template v-slot:content>
            <ClientEmptyState
              :title="tituloSemSelecao"
              :description="descricaoSemSelecao"
            />

            <div class="d-flex align-center justify-center az-gap mt-4">
              <BaseButton
                v-if="!ehVisaoDeGrupo"
                outline
                primary
                @click="handleOpenNewClient"
              >
                Cadastrar cliente
              </BaseButton>
              <BaseButton primary @click="handleAcaoPrimariaSemSelecao">
                {{ rotuloAcaoSemSelecao }}
              </BaseButton>
            </div>
          </template>
        </FeedbackWrapper>
      </v-col>
    </v-row>

    <v-row v-if="shouldShowNoAccessCard">
      <v-col cols="12">
        <div class="wallet-empty-state__card az-card-consolidator">
          <div class="wallet-empty-state__card-title">
            Você não tem acesso a esse cliente
          </div>

          <div class="wallet-empty-state__card-description">
            Envie uma proposta de serviço para esse cliente e tenha acesso a
            posição dele após a contratação.
          </div>

          <div
            class="wallet-empty-state__card-action mt-5"
            @click="handleSendEmail"
          >
            <v-icon x-large color="primary"> mdi-bank </v-icon>
            <div class="text-center">
              Envie convite para ele acessar e cadastrar
              <strong style="color: var(--primary)">
                contas automáticas
              </strong>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="shouldShowNoClientsRegisteredCard">
      <v-col cols="12">
        <div class="wallet-empty-state__card az-card-consolidator">
          <div class="wallet-empty-state__card-title">
            Você não possui nenhum cliente cadastrado
          </div>

          <div class="wallet-empty-state__card-description">
            Cadastre seus clientes e envie link dos seus serviços para ele
            acessar a plataforma/aplicativo e você acompanhar
            <strong style="color: var(--primary)"
              >100% do share da carteira dele</strong
            >, independente da instituição.
          </div>

          <div class="d-flex az-gap">
            <div
              class="wallet-empty-state__card-action mt-5"
              @click="handleCollectClientDefault"
            >
              <v-icon x-large color="primary"> mdi-account-arrow-left </v-icon>
              <div class="text-center">
                Inserir
                <strong style="color: var(--primary)"> cliente default </strong>
                com meus dados
              </div>
            </div>

            <div
              class="wallet-empty-state__card-action mt-5"
              @click="handleOpenNewClient"
            >
              <v-icon x-large color="primary"> mdi-account </v-icon>
              <div class="text-center">
                Cadastre um <br />
                <strong style="color: var(--primary)">
                  cliente individual
                </strong>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="shouldShowNoAssetsCard">
      <v-col cols="12">
        <FeedbackWrapper
          :noData="true"
          icon-name="wallet-empty"
          icon-color="primary"
          class="az-card-consolidator"
        >
          <template v-slot:content>
            <ClientEmptyState
              :title="rescueMessage"
              description="Cadastre os ativos dele para visualizar o consolidador da carteira atual."
            />

            <div class="d-flex align-center justify-center az-gap mt-4">
              <BaseButton outline primary @click="handleImportAssets">
                Importar
              </BaseButton>
              <BaseButton primary @click="handleNewAsset">
                Cadastrar Ativo
              </BaseButton>
            </div>
          </template>
        </FeedbackWrapper>
      </v-col>
    </v-row>

    <v-row v-if="shouldShowClientNoAssetsCard">
      <v-col cols="12">
        <FeedbackWrapper
          :loading="false"
          :noData="true"
          class="az-card-consolidator"
        >
          <template v-slot:content>
            <ClientEmptyState
              title="Você ainda não possui ativos cadastrados"
              description="Entre em contato com o seu profissional para cadastrar seus ativos e acompanhar sua carteira de investimentos."
            />
          </template>
        </FeedbackWrapper>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="sass">
.wallet-empty-state
  &__card
    display: flex
    justify-content: center
    align-items: center
    flex-direction: column
    padding: 64px

    &-title
      color: #5B5B5B
      font-weight: 600 !important
      font-size: 16px

    &-description
      font-size: 14px
      max-width: 600px
      text-align: center
      margin-top: 18px

    &-action
      background-color: #f4f4f4 !important
      border: 1px solid var(--primary)
      height: 17vw
      width: 14vw
      cursor: pointer
      align-items: center
      justify-content: center
      display: flex
      flex-direction: column
      gap: 16px
      border-radius: 16px
      padding: 4px
</style>
